<!-- Modal mode: fixed overlay + centered card + title + save/cancel.
     The Add-User drawer's per-scanner Claims sub-panel uses [flat]=true
     instead — see the flat block at the bottom. -->
<div *ngIf="!flat" class="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
     (click)="onCancel()">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg w-[640px] max-w-[95vw] max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <!-- Header: title + user identity (name big, email small + muted)
         underneath. Replaces the prior pattern of appending the
         username/UUID to the title string — Mike 2026-06-26: "the user
         number at the top should be replaced with name; sub text email." -->
    <div class="py-3 px-6 dark:bg-app-black-100 rounded-t">
      <h1 class="text-md font-semibold leading-tight tracking-tight text-gray-900 md:text-xl dark:text-white">
        <i class="fa-solid fa-key text-app-teal-100 mr-2"></i> {{ title }}
      </h1>
      <div *ngIf="userDisplayName || userEmail" class="mt-1 leading-tight">
        <div *ngIf="userDisplayName" class="text-sm font-medium text-gray-800 dark:text-gray-100">
          {{ userDisplayName }}
        </div>
        <div *ngIf="userEmail" class="text-xs text-gray-500 dark:text-gray-400">
          {{ userEmail }}
        </div>
      </div>
    </div>

    <div class="py-4 px-6 dark:bg-gradient-to-b dark:from-app-black-200 dark:to-app-black-300 text-sm">
      <ng-container *ngTemplateOutlet="bodyTpl"></ng-container>
      <div class="mt-6 flex gap-2 justify-end">
        <button type="button" (click)="onCancel()" class="dark-btn-secondary text-xs px-3 py-1">cancel</button>
        <button type="button" (click)="onSave()" class="dark-btn-primary text-xs px-3 py-1">save</button>
      </div>
    </div>
  </div>
</div>

<!-- Flat/inline mode (IT-F2-106 Phase 3b): render just the card body,
     no overlay, no title bar, no save/cancel. Parent controls the outer
     enable state (scanner checkbox in the Add-User drawer). -->
<div *ngIf="flat" class="text-sm">
  <ng-container *ngTemplateOutlet="bodyTpl"></ng-container>
</div>

<ng-template #bodyTpl>
  <p *ngIf="parseError" class="mb-3 text-xs text-red-400 bg-red-900/30 border border-red-500/40 rounded px-2 py-1">
    {{ parseError }}
  </p>

  <p *ngIf="!parseError && cards.length === 0 && orphans.length === 0"
     class="mb-4 text-xs text-gray-500 dark:text-gray-400">
    This user isn't entitled to any scanner whose customer defines a claims_schema.
    Assign scanners to the user via the Scanners dialog first.
  </p>

  <!-- Outer box: one per CUSTOMER. Inside, one card per SCANNER
       (Mike 2026-06-28 contract: visibility driven by the user's
       custom:scanners value). Wildcards like 'tt-*' fan out into
       one card per matched scanner inside the customer box.
       In flat mode we skip the customer-box chrome — the parent
       (per-scanner accordion in Add-User) already owns that context. -->
  <div *ngFor="let group of cardGroups"
       class="border rounded-md"
       [class.mb-4]="!flat"
       [class.border-gray-300]="!flat"
       [class.dark:border-app-black-500]="!flat"
       [class.dark:bg-app-black-500]="!flat"
       [class.border-transparent]="flat"
       [class.bg-transparent]="flat">
    <div *ngIf="!flat" class="px-3 py-2 border-b border-gray-200 dark:border-app-black-500 flex items-center gap-2">
      <i class="fa-solid fa-building text-app-teal-100 text-xs"></i>
      <span class="text-xs font-semibold text-gray-800 dark:text-gray-100">
        {{ group.customer.name || group.customer._id }}
      </span>
      <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ group.customer._id }}
      </span>
      <span class="ml-auto text-[10px] uppercase tracking-wide text-gray-400">
        {{ group.cards.length }} scanner{{ group.cards.length === 1 ? '' : 's' }}
      </span>
    </div>
    <div [class.p-3]="!flat" [class.p-0]="flat" class="flex flex-col gap-2">

  <div *ngFor="let card of group.cards"
       class="border rounded transition-colors"
       [ngClass]="card.enabled
         ? 'border-app-teal-100 bg-white dark:bg-app-black-100'
         : 'border-gray-300 dark:border-app-black-100 bg-white dark:bg-app-black-100'"
       [class.border-transparent]="flat"
       [class.dark:border-app-black-100]="flat">

    <!-- Header: checkbox + scanner name + customer subtitle. Clicking
         anywhere on the header toggles the card. Hidden in flat mode
         (parent's outer scanner-enable checkbox IS the gate). -->
    <div *ngIf="!flat" class="flex items-center justify-between px-3 py-2 cursor-pointer select-none"
         (click)="onHeaderToggle(card)">
      <label class="flex items-center gap-2 cursor-pointer min-w-0"
             (click)="$event.stopPropagation()">
        <input type="checkbox"
               [checked]="card.enabled"
               (change)="onToggleCard(card, $any($event.target).checked)"
               class="cursor-pointer flex-shrink-0" />
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate"
                [ngClass]="card.enabled
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-700 dark:text-gray-300'">
            {{ card.scannerName || card.scannerId }}
          </span>
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate">
            {{ cardSubtitle(card) }}
          </span>
        </div>
      </label>
      <div class="flex items-center gap-2 flex-shrink-0">
        <span *ngIf="card.enabled" class="text-[11px] text-gray-500 dark:text-gray-400">
          {{ cardSummary(card) }}
        </span>
        <i class="fa-solid text-xs text-gray-500 dark:text-gray-400"
           [class.fa-chevron-down]="card.enabled"
           [class.fa-chevron-right]="!card.enabled"></i>
      </div>
    </div>

    <!-- Expanded body — only when enabled (always true in flat mode).
         Card scope = one scanner; no scanner picker since the card
         IS the scanner. -->
    <div *ngIf="card.enabled"
         [class.px-3]="!flat" [class.pb-3]="!flat"
         [class.border-t]="!flat" [class.border-gray-200]="!flat" [class.dark:border-app-black-100]="!flat">
      <div [class.mt-3]="!flat" [class.border]="!flat" [class.border-gray-200]="!flat" [class.dark:border-app-black-500]="!flat" [class.rounded]="!flat" [class.p-3]="!flat">

        <!-- Schema-driven field renderers. Ngmodel binds trigger auto-
             emit-save via _emitIfFlat() on the type-specific handlers
             (onStringBoolChange, toggleArrayValue, onGroupSelectChange).
             For scalar int/string/bool the ngModelChange fires
             _emitIfFlat() inline. -->
        <div *ngFor="let f of card.schema.fields" class="mb-3">
          <label class="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            {{ f.label }}
            <span *ngIf="f.required" class="text-red-400 ml-0.5">*</span>
          </label>

          <select *ngIf="f.type === 'int' && f.enum"
                  [(ngModel)]="card.entry.raw[f.key]"
                  (ngModelChange)="_emitIfFlat()"
                  class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full">
            <option *ngFor="let o of f.enum" [ngValue]="o.value">{{ o.label }}</option>
          </select>
          <input *ngIf="f.type === 'int' && !f.enum" type="number"
                 [(ngModel)]="card.entry.raw[f.key]"
                 (ngModelChange)="_emitIfFlat()"
                 class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full" />

          <label *ngIf="f.type === 'bool'" class="inline-flex items-center gap-2 text-xs text-gray-900 dark:text-white cursor-pointer">
            <input type="checkbox" [(ngModel)]="card.entry.raw[f.key]" (ngModelChange)="_emitIfFlat()" />
            <span>enabled</span>
          </label>

          <select *ngIf="f.type === 'stringbool'"
                  [ngModel]="card.entry.raw[f.key]" (ngModelChange)="onStringBoolChange(card.entry, f, $event)"
                  class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full">
            <option *ngFor="let o of stringBoolOptions(f)" [ngValue]="o.value">{{ o.label }}</option>
          </select>

          <select *ngIf="f.type === 'string' && f.enum"
                  [(ngModel)]="card.entry.raw[f.key]"
                  (ngModelChange)="_emitIfFlat()"
                  class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full">
            <option *ngFor="let o of f.enum" [ngValue]="o.value">{{ o.label }}</option>
          </select>
          <input *ngIf="f.type === 'string' && !f.enum" type="text"
                 [(ngModel)]="card.entry.raw[f.key]"
                 (ngModelChange)="_emitIfFlat()"
                 class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full" />

          <!-- Grouped-mode renderer: when enum labels follow a
               "Group - Mode" convention, render one dropdown per
               group with mutually-exclusive modes (Disabled +
               per-mode options). Auto-detected by isGroupedArrayField. -->
          <div *ngIf="f.type === 'array' && f.enum && isGroupedArrayField(f)" class="flex flex-col gap-2">
            <div *ngFor="let g of arrayFieldGroups(f)" class="flex items-center gap-2">
              <span class="text-xs text-gray-700 dark:text-gray-200 font-medium min-w-[140px] flex-shrink-0">{{ g.group }}</span>
              <select class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs flex-1"
                      [ngModel]="getGroupSelectedValue(card.entry, f, g.options)"
                      (ngModelChange)="onGroupSelectChange(card.entry, f, g.options, $event)">
                <option [ngValue]="''">Disabled</option>
                <option *ngFor="let o of g.options" [ngValue]="o.value">{{ o.modeLabel }}</option>
              </select>
            </div>
          </div>

          <!-- Flat multi-select renderer — for array+enum fields
               whose labels don't follow the grouped convention. -->
          <div *ngIf="f.type === 'array' && f.enum && !isGroupedArrayField(f)" class="flex flex-wrap gap-2">
            <label *ngFor="let o of f.enum"
                   class="inline-flex items-center gap-1.5 text-xs text-gray-900 dark:text-white border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5 cursor-pointer hover:border-app-teal-100">
              <input type="checkbox"
                     [checked]="isArrayValueSelected(card.entry, f, o.value)"
                     (change)="toggleArrayValue(card.entry, f, o.value)" />
              <span>{{ o.label }}</span>
            </label>
          </div>
          <div *ngIf="f.type === 'array' && f.options && !f.enum" class="flex flex-wrap gap-2">
            <label *ngFor="let opt of f.options"
                   class="inline-flex items-center gap-1.5 text-xs text-gray-900 dark:text-white border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5 cursor-pointer hover:border-app-teal-100">
              <input type="checkbox"
                     [checked]="isArrayValueSelected(card.entry, f, opt)"
                     (change)="toggleArrayValue(card.entry, f, opt)" />
              <span>{{ opt }}</span>
            </label>
          </div>

          <p *ngIf="f.description" class="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            {{ f.description }}
          </p>
        </div>
      </div>
    </div>
  </div>
    </div>
  </div>

  <!-- Orphan entries: claims that target customers the user is no longer
       entitled to, OR scanners with no schema. Surfaced so the admin
       can prune them rather than silently dropping them on save. Hidden
       in flat mode since Add-User creation flow starts with no
       pre-existing claims JSON. -->
  <div *ngIf="orphans.length && !flat" class="mt-5">
    <h3 class="text-xs uppercase tracking-wide text-amber-500 mb-2 flex items-center gap-2">
      <i class="fa-solid fa-triangle-exclamation"></i>
      Other entries
      <span class="text-[10px] normal-case text-gray-500 dark:text-gray-400">
        (user not entitled to these customers, or no schema)
      </span>
    </h3>
    <div *ngFor="let o of orphans; let i = index"
         class="mb-3 border border-amber-500/40 rounded p-3 bg-amber-500/5">
      <div class="flex items-center justify-between mb-2">
        <span class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {{ o.customerSlug || 'unknown customer' }} — scanner {{ o.raw?.scanner || '?' }}
        </span>
        <button type="button" (click)="onRemoveOrphan(i)"
                class="text-red-400 hover:text-red-300 text-xs">
          <i class="fa-solid fa-trash"></i> remove
        </button>
      </div>
      <textarea rows="5"
                [(ngModel)]="o.fallbackJson"
                class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded p-2 text-xs font-mono w-full"></textarea>
      <p *ngIf="o.fallbackError" class="mt-1 text-xs text-red-400">{{ o.fallbackError }}</p>
    </div>
  </div>
</ng-template>
